'use client';

import { type ReactNode } from 'react';

export interface MoneyProps {
  /**
   * Integer minor units (cents) — HeyRoot's money contract. Accepts a string
   * (canonical), number, or bigint. `null`/`undefined`/`''` and values that
   * fail integer parsing (e.g. decimal strings like `'12.5'`) render the
   * unset placeholder — this primitive never silently truncates a fraction.
   */
  amountCents: string | number | bigint | null | undefined;
  currency?: string;
  locale?: string;
  /**
   * `total` = emphasized via the metric-sm token (size + token-baked weight)
   * + tone, per doc 03 (owner ruling 2026-07-06). Raw `font-*` utilities stay
   * banned; the token's baked weight is the sanctioned mechanism.
   */
  variant?: 'default' | 'total' | 'muted' | 'prominent';
  /** Show a leading `+` on positive amounts (deltas, credits). */
  showPositiveSign?: boolean;
  /**
   * Negative rendering. Default `'minus'` (`-$5.00`). `'parens'` renders
   * `($5.00)` in danger tone — finance surfaces (statements, ledgers) opt in
   * per doc 03 (owner ruling 2026-07-06). `showPositiveSign` is unaffected;
   * parens mode only changes how negatives are drawn.
   */
  negativeDisplay?: 'minus' | 'parens';
  /**
   * Semantic tone for the amount, independent of its sign. `'danger'` renders any amount --
   * including a positive one, such as the credit side of an unbalanced journal -- in
   * `text-danger-text`. It REPLACES the variant's tone rather than stacking a second colour
   * utility on one element (see VARIANT_TONE). Scale, tabular figures, formatting and
   * negative display are untouched. Omit for the variant's own tone.
   */
  tone?: 'danger';
  /** Rendered when the amount is unset (null/undefined/''). Default `—`. */
  unset?: ReactNode;
  className?: string;
}

// Tone and scale are separate axes on purpose (Typography v4 PR 3, #12011).
// They used to be one string per variant, which made the parens-negative branch
// below APPEND `text-danger-text` to a class list that already carried
// `text-primary`. Both landed on the same element, so the winner was decided by
// rule order in the compiled stylesheet rather than by intent -- and
// `text-primary` won, rendering a credit in the same colour as a positive
// amount. Splitting the axes lets that branch REPLACE the tone instead of
// fighting it. Spike B's accepted recipe assigned exactly this edit to PR 3
// (evidence/spike-b/treatment-c2.css and selection.verdict.md: "Money parens
// negative -> text-danger-text (drop the text-primary that wins today)").
//
// Concatenation order below is `tone` then `scale`, which reproduces the old
// single-string order byte-for-byte for every non-parens render.
const VARIANT_TONE: Record<NonNullable<MoneyProps['variant']>, string> = {
  default: 'text-primary',
  total: 'text-primary',
  muted: 'text-muted',
  prominent: 'text-primary',
};

const VARIANT_SCALE: Record<NonNullable<MoneyProps['variant']>, string> = {
  default: '',
  total: 'text-metric-sm',
  muted: '',
  // `prominent` = the dominant money moment on a surface (the settled total on
  // a payment-complete screen), via the metric-lg token. Distinct from `total`,
  // the in-panel emphasis rank. ADR-0126 retired the Command Deck's "one bold
  // typographic moment per view" cap and states hierarchy comes from scale and
  // weight rather than colour scarcity — so this carries scale only and
  // deliberately stays on `text-primary`: brand green is reserved for the
  // primary CTA (DESIGN.md §Color discipline) and the success token is
  // status-signal-only, so neither may be spent on a value.
  //
  // Deliberately NOT named `hero`: `--text-metric-hero` (3.25rem/52px) is a
  // real, distinct rank from `--text-metric-lg` (2.25rem/36px). A shared
  // primitive exposing `variant="hero"` that renders the *lg* rank would be a
  // permanently misleading API for every future consumer.
  prominent: 'text-metric-lg',
};

/**
 * Parse minor units into a bigint, or `null` if the value isn't a clean
 * integer. Bigints pass through; whole numbers convert; decimal strings
 * (`'12.5'`) and non-numeric strings fail to `null` (empty strings are caught
 * by the unset guard before parsing) so
 * the caller falls back to the unset placeholder instead of truncating.
 */
function parseCents(v: string | number | bigint): bigint | null {
  try {
    if (typeof v === 'bigint') return v;
    if (typeof v === 'number') return Number.isInteger(v) ? BigInt(v) : null;
    return BigInt(v); // throws on '12.5', 'abc'
  } catch {
    return null;
  }
}

/**
 * Money display (GATE C.1). Symbol always shown; negatives default to
 * `-$5.00` (minus); finance surfaces may opt into `($5.00)` parens (danger
 * tone) via `negativeDisplay="parens"` per doc 03 (owner ruling 2026-07-06);
 * real zero `$0.00`; unset renders `—`. Tabular sans by default — totals
 * emphasized via the metric-sm token (size + token-baked weight) + tone per
 * doc 03 (owner ruling 2026-07-06). A call site (e.g. the checkout focal
 * amount) may still opt into the mono family via `className="font-mono"`;
 * the `money-figure` class (always applied, un-layered in tokens.css) pins
 * `font-feature-settings` to `--font-features-money` regardless, so the
 * figures stay plain-zero even when the family switches to mono.
 */
export function Money({
  amountCents,
  currency = 'USD',
  locale = 'en-US',
  variant = 'default',
  showPositiveSign = false,
  negativeDisplay = 'minus',
  tone,
  unset = '—',
  className,
}: MoneyProps) {
  // Array/join construction, never a template literal: a utility adjacent to an
  // interpolation is the class-extraction hazard check-tailwind-class-interpolation.cjs guards.
  const classesFor = (tone: string): string =>
    ['money-figure', 'tabular-nums', tone, VARIANT_SCALE[variant], className]
      .filter(Boolean)
      .join(' ');

  const cls = classesFor(tone === 'danger' ? 'text-danger-text' : VARIANT_TONE[variant]);

  if (amountCents == null || amountCents === '') {
    return <span className={cls}>{unset}</span>;
  }

  const cents = parseCents(amountCents);
  if (cents === null) {
    return <span className={cls}>{unset}</span>;
  }

  const isNegative = cents < 0n;
  const useParens = negativeDisplay === 'parens' && isNegative;

  // Values are converted to a JS number for Intl formatting — safe for any
  // amount that fits Number.MAX_SAFE_INTEGER cents (~$90 trillion).
  const magnitude = useParens ? -cents : cents;
  // In parens mode the magnitude is positive, so `exceptZero` would emit a
  // spurious leading `+` inside the parens (`(+$5.00)`). Force plain sign
  // display there; `showPositiveSign` still adds `+` to real positives.
  // `Intl.NumberFormat` throws `RangeError` on a currency code that is not three
  // alphabetic characters, and `currency` arrives from the wire. Before Typography v4
  // PR 3 (#12011) the POS checkout's remaining balance rendered through
  // `formatPosPrice`, which wrapped this exact construction in try/catch; moving that
  // figure onto `<Money>` would otherwise have turned a malformed server value into a
  // THROW DURING RENDER on the payment surface. `checkout-sheet.tsx`'s own #10165
  // boundary comment exists because that failure class walks an operator out of the
  // register mid-sale, so degrading is strictly better than propagating here.
  //
  // The fallback keeps the amount readable and correctly signed and simply omits the
  // symbol it could not resolve — the operator still sees the number. Deliberately NOT
  // hardcoding `$`: inventing a currency symbol for an unparseable code would state
  // something false about money.
  //
  // It degrades in TWO stages, because dropping straight to a hand-built string also drops
  // digit grouping: an independent judge measured this surface's own pilot balance rendering
  // as `12380.55` instead of `12,380.55`, and on a 36px focal payment figure the thousands
  // separator is what prevents an order-of-magnitude misread. A `style: 'decimal'` formatter
  // restores grouping AND locale-correct separators (de-DE -> `12.380,55`) and structurally
  // cannot hit the currency RangeError, because it never receives the currency code. Only a
  // malformed `locale` defeats that too, which is the one case the manual string handles.
  const signDisplay = showPositiveSign && !useParens ? 'exceptZero' : 'auto';
  const asNumber = Number(magnitude) / 100;
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      signDisplay,
    }).format(asNumber);
  } catch {
    // Dev-gated, mirroring resolveActionVerb in Button.tsx: a malformed wire currency can
    // otherwise persist unnoticed, since an unsymboled `1420.00` still reads as a formatted
    // figure to a US-only operator. Surfaces the data defect without changing what renders.
    if (process.env['NODE_ENV'] !== 'production') {
      console.warn(
        `[Money] Intl could not format currency ${JSON.stringify(currency)} (locale ${JSON.stringify(locale)}) — rendering the amount without a symbol.`,
      );
    }
    try {
      formatted = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay,
      }).format(asNumber);
    } catch {
      const abs = magnitude < 0n ? -magnitude : magnitude;
      const whole = abs / 100n;
      const minor = (abs % 100n).toString().padStart(2, '0');
      const sign =
        magnitude < 0n ? '-' : showPositiveSign && !useParens && magnitude > 0n ? '+' : '';
      formatted = `${sign}${whole}.${minor}`;
    }
  }

  if (useParens) {
    // REPLACES the variant tone rather than appending to it -- see VARIANT_TONE above.
    return <span className={classesFor('text-danger-text')}>({formatted})</span>;
  }

  return <span className={cls}>{formatted}</span>;
}
